import { db } from '../firebase';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';

class FavoritesManager {
	constructor() {
		this.pendingUpdates = {
			players: new Set(),
			activities: new Set()
		};
		this.pendingRemovals = {
			players: new Set(),
			activities: new Set()
		};
		this.updateTimer = null;
		this.isProcessing = false;
		this.DEBOUNCE_DELAY = 3000; // 3 seconds
		this.MAX_FAVORITE_PLAYERS = 21; // Maximum number of favorite players allowed
		
		// Track processing state for UI feedback
		this.processingItems = {
			players: new Set(),
			activities: new Set()
		};
		
		// Event listeners for UI updates
		this.listeners = [];
	}
	
	// Add event listener for UI updates
	addEventListener(callback) {
		this.listeners.push(callback);
	}
	
	// Remove event listener
	removeEventListener(callback) {
		this.listeners = this.listeners.filter(listener => listener !== callback);
	}
	
	// Emit events to listeners
	emit(event, data) {
		this.listeners.forEach(listener => listener(event, data));
	}
	
	// Convert singular item type to plural for internal storage
	normalizeItemType(itemType) {
		if (itemType === 'player') return 'players';
		if (itemType === 'activity') return 'activities';
		if (itemType === 'players') return 'players';
		if (itemType === 'activities') return 'activities';
		return itemType; // fallback
	}

	// Check if adding a player would exceed the limit
	isAtPlayersLimit(currentFavoritePlayers = []) {
		// Calculate current local count including pending changes
		const currentCount = this.getLocalPlayersCount(currentFavoritePlayers);
		return currentCount >= this.MAX_FAVORITE_PLAYERS;
	}
	
	// Get the current local count of favorite players (including pending changes)
	getLocalPlayersCount(currentFavoritePlayers = []) {
		// Start with current Firebase count
		let count = currentFavoritePlayers.length;
		
		// Add pending additions
		count += this.pendingUpdates.players.size;
		
		// Subtract pending removals
		count -= this.pendingRemovals.players.size;
		
		return Math.max(0, count); // Ensure never negative
	}
	
	// Check if a specific player would cause limit to be exceeded
	wouldExceedPlayersLimit(currentFavoritePlayers = [], playerId) {
		// If it's already a favorite (in Firebase or pending addition), it won't increase count
		const normalizedId = this.normalizeId(playerId, 'player');
		
		// Check if already in Firebase favorites
		const isInFirebase = currentFavoritePlayers.some(id => String(id) === String(normalizedId));
		if (isInFirebase) {
			return false; // Already a favorite, won't increase count
		}
		
		// Check if already in pending additions
		if (this.pendingUpdates.players.has(normalizedId)) {
			return false; // Already pending addition, won't increase count
		}
		
		// Check if in pending removals (would be net zero change if we add it back)
		if (this.pendingRemovals.players.has(normalizedId)) {
			return false; // Would be removing then adding, net zero
		}
		
		// This would be a new addition, check if it would exceed limit
		const currentCount = this.getLocalPlayersCount(currentFavoritePlayers);
		return (currentCount + 1) > this.MAX_FAVORITE_PLAYERS;
	}

	// Add item to favorites (local state + debounced Firebase update)
	addToFavorites(userId, itemId, itemType, currentFavorites = []) {
		// console.log(`🟢 Adding ${itemType} ${itemId} to favorites (batched)`);
		
		// Normalize item type to plural form
		const normalizedItemType = this.normalizeItemType(itemType);
		// console.log(`🔄 Normalized item type: ${itemType} -> ${normalizedItemType}`);
		
		// Normalize item ID
		const normalizedId = this.normalizeId(itemId, itemType);
		// console.log(`🔄 Normalized ID: ${normalizedId}`);
		
		// Check player limit before adding
		if (itemType === 'player' && this.wouldExceedPlayersLimit(currentFavorites, itemId)) {
			// console.log(`❌ Cannot add player ${itemId}: would exceed limit of ${this.MAX_FAVORITE_PLAYERS}`);
			
			// Emit limit exceeded event
			this.emit('players-limit-exceeded', {
				limit: this.MAX_FAVORITE_PLAYERS,
				currentCount: this.getLocalPlayersCount(currentFavorites)
			});
			
			return false; // Failed due to limit
		}
		
		// Add to pending updates and remove from pending removals
		this.pendingUpdates[normalizedItemType].add(normalizedId);
		this.pendingRemovals[normalizedItemType].delete(normalizedId);
		
		// console.log(`📝 Pending updates for ${normalizedItemType}:`, Array.from(this.pendingUpdates[normalizedItemType]));
		// console.log(`🗑️ Pending removals for ${normalizedItemType}:`, Array.from(this.pendingRemovals[normalizedItemType]));
		
		// Check if this item has any pending changes (if not, it's back to original state)
		const hasPendingChanges = this.pendingUpdates[normalizedItemType].has(normalizedId) || 
								  this.pendingRemovals[normalizedItemType].has(normalizedId);
		
		if (hasPendingChanges) {
			// Add to processing state for UI feedback
			this.processingItems[normalizedItemType].add(normalizedId);
			
			// Emit processing state change (use original itemType for component compatibility)
			this.emit('processing-state-changed', {
				type: itemType,
				id: normalizedId,
				isProcessing: true
			});
		} else {
			// No pending changes, clear processing state
			this.processingItems[normalizedItemType].delete(normalizedId);
			
			this.emit('processing-state-changed', {
				type: itemType,
				id: normalizedId,
				isProcessing: false
			});
		}
		
		// Emit limit status change for players (addition might reach limit)
		if (itemType === 'player') {
			this.emit('players-limit-changed', {
				isAtLimit: this.isAtPlayersLimit(currentFavorites),
				currentCount: this.getLocalPlayersCount(currentFavorites)
			});
		}
		
		// Reset the debounce timer
		this.resetUpdateTimer(userId);
		
		return true; // Immediate local success
	}
	
	// Remove item from favorites (local state + debounced Firebase update)
	removeFromFavorites(userId, itemId, itemType, currentFavorites = []) {
		// console.log(`🔴 Removing ${itemType} ${itemId} from favorites (batched)`);
		
		// Normalize item type to plural form
		const normalizedItemType = this.normalizeItemType(itemType);
		// console.log(`🔄 Normalized item type: ${itemType} -> ${normalizedItemType}`);
		
		// Normalize item ID
		const normalizedId = this.normalizeId(itemId, itemType);
		// console.log(`🔄 Normalized ID: ${normalizedId}`);
		
		// Add to pending removals and remove from pending updates
		this.pendingRemovals[normalizedItemType].add(normalizedId);
		this.pendingUpdates[normalizedItemType].delete(normalizedId);
		
		//
		
		// Check if this item has any pending changes (if not, it's back to original state)
		const hasPendingChanges = this.pendingUpdates[normalizedItemType].has(normalizedId) || 
								  this.pendingRemovals[normalizedItemType].has(normalizedId);
		
		if (hasPendingChanges) {
			// Add to processing state for UI feedback
			this.processingItems[normalizedItemType].add(normalizedId);
			
			// Emit processing state change (use original itemType for component compatibility)
			this.emit('processing-state-changed', {
				type: itemType,
				id: normalizedId,
				isProcessing: true
			});
		} else {
			// No pending changes, clear processing state
			this.processingItems[normalizedItemType].delete(normalizedId);
			
			this.emit('processing-state-changed', {
				type: itemType,
				id: normalizedId,
				isProcessing: false
			});
		}
		
		// Emit limit status change for players (removal might free up space)
		if (itemType === 'player') {
			this.emit('players-limit-changed', {
				isAtLimit: this.isAtPlayersLimit(currentFavorites),
				currentCount: this.getLocalPlayersCount(currentFavorites)
			});
		}
		
		// Reset the debounce timer
		this.resetUpdateTimer(userId);
		
		return true; // Immediate local success
	}
	
	// Normalize ID based on item type
	normalizeId(itemId, itemType) {
		if (itemType === 'activity') {
			// For activities, prefer numeric ID if possible
			const numId = parseInt(itemId, 10);
			if (!isNaN(numId)) {
				return numId; // Store as number for activities
			}
		}
		// For players or non-numeric activities, use string
		return String(itemId);
	}
	
	// Reset the update timer (debounce mechanism)
	resetUpdateTimer(userId) {
		// Clear existing timer
		if (this.updateTimer) {
			clearTimeout(this.updateTimer);
		}
		
		// Set new timer
		this.updateTimer = setTimeout(() => {
			this.flushUpdatesToFirebase(userId);
		}, this.DEBOUNCE_DELAY);
		
		// console.log(`⏰ Update timer reset. Will flush to Firebase in ${this.DEBOUNCE_DELAY}ms`);
	}
	
	// Flush all pending updates to Firebase
	async flushUpdatesToFirebase(userId) {
		if (this.isProcessing) {
			// console.log('Already processing updates, skipping...');
			return;
		}
		
		this.isProcessing = true;
		// console.log('🚀 Flushing pending updates to Firebase...');
		
		try {
			const userDocRef = doc(db, 'userData', userId);
			
			// Get current document
			const docSnap = await getDoc(userDocRef);
			let currentData = {};
			
			if (docSnap.exists()) {
				currentData = docSnap.data();
			}
			
			// Process players
			await this.processItemType('players', 'favoritePlayers', 'favoritePlayersLastUpdate', currentData, userDocRef);
			
			// Process activities
			await this.processItemType('activities', 'favoriteActivities', 'favoriteActivitiesLastUpdate', currentData, userDocRef);
			
			// console.log('All updates flushed to Firebase successfully');
			
		} catch (error) {
			// console.error('Error flushing updates to Firebase:', error);
			
			// Emit error event
			this.emit('update-error', { error });
		} finally {
			this.isProcessing = false;
			
			// Clear processing states
			this.processingItems.players.clear();
			this.processingItems.activities.clear();
			
			// Emit processing complete
			this.emit('processing-complete');
		}
	}
	
	// Process updates for a specific item type
	async processItemType(itemType, fieldName, timestampFieldName, currentData, userDocRef) {
		const pendingAdds = Array.from(this.pendingUpdates[itemType]);
		const pendingRemoves = Array.from(this.pendingRemovals[itemType]);
		
		if (pendingAdds.length === 0 && pendingRemoves.length === 0) {
			return; // Nothing to update for this type
		}
		
		// console.log(`Processing ${itemType}:`, {
		// 	adds: pendingAdds,
		// 	removes: pendingRemoves
		// });
		
		// Get current favorites or empty array
		let currentFavorites = currentData[fieldName] || [];
		
		// Ensure all current favorites are properly typed for comparison
		currentFavorites = currentFavorites.map(id => {
			if (itemType === 'activities') {
				const numId = parseInt(id, 10);
				return !isNaN(numId) ? numId : String(id);
			}
			return String(id);
		});
		
		// Apply removals
		pendingRemoves.forEach(removeId => {
			currentFavorites = currentFavorites.filter(id => {
				if (itemType === 'activities') {
					// For activities, compare both numeric and string forms
					return String(id) !== String(removeId) && id !== removeId;
				}
				return String(id) !== String(removeId);
			});
		});
		
		// Apply additions
		pendingAdds.forEach(addId => {
			// Check if not already in favorites
			const alreadyExists = currentFavorites.some(id => {
				if (itemType === 'activities') {
					return String(id) === String(addId) || id === addId;
				}
				return String(id) === String(addId);
			});
			
			if (!alreadyExists) {
				currentFavorites.push(addId);
			}
		});
		
		// Update Firebase
		const updateObj = {
			[fieldName]: currentFavorites,
			[timestampFieldName]: serverTimestamp()
		};
		
		if (currentData && Object.keys(currentData).length > 0) {
			// Update existing document
			await updateDoc(userDocRef, updateObj);
		} else {
			// Create new document
			await setDoc(userDocRef, updateObj);
		}
		
		// console.log(`${itemType} updated in Firebase:`, currentFavorites);
		
		// Clear pending updates for this type
		this.pendingUpdates[itemType].clear();
		this.pendingRemovals[itemType].clear();
		
		// Clear processing state for this type
		this.processingItems[itemType].clear();
	}
	
	// Check if an item is currently being processed
	isItemProcessing(itemId, itemType) {
		// Validate inputs
		if (!itemId || !itemType) {
			return false;
		}
		
		// Normalize item type to plural form
		const normalizedItemType = this.normalizeItemType(itemType);
		
		// Ensure itemType is valid
		const validItemTypes = ['players', 'activities'];
		if (!validItemTypes.includes(normalizedItemType)) {
			return false;
		}
		
		// Ensure the set exists for this item type
		if (!this.processingItems[normalizedItemType]) {
			return false;
		}
		
		const normalizedId = this.normalizeId(itemId, itemType);
		return this.processingItems[normalizedItemType].has(normalizedId);
	}
	
	// Get current local state (including pending changes)
	getLocalFavoriteState(itemId, itemType, currentFirebaseState) {
		// Validate inputs
		if (!itemId || !itemType) {
			// console.warn('getLocalFavoriteState: Invalid itemId or itemType', { itemId, itemType });
			return currentFirebaseState;
		}
		
		// Normalize item type to plural form
		const normalizedItemType = this.normalizeItemType(itemType);
		
		// Ensure itemType is valid
		const validItemTypes = ['players', 'activities'];
		if (!validItemTypes.includes(normalizedItemType)) {
			// console.warn('getLocalFavoriteState: Invalid itemType', itemType, 'normalized to', normalizedItemType);
			return currentFirebaseState;
		}
		
		const normalizedId = this.normalizeId(itemId, itemType);
		
		// Ensure the sets exist for this item type
		if (!this.pendingUpdates[normalizedItemType] || !this.pendingRemovals[normalizedItemType]) {
			// 	console.warn('getLocalFavoriteState: Pending sets not initialized for', normalizedItemType);
			return currentFirebaseState;
		}
		
		// Check pending updates
		if (this.pendingUpdates[normalizedItemType].has(normalizedId)) {
			return true; // Will be added
		}
		
		// Check pending removals
		if (this.pendingRemovals[normalizedItemType].has(normalizedId)) {
			return false; // Will be removed
		}
		
		// Return current Firebase state
		return currentFirebaseState;
	}
	
	// Force immediate flush (for when user navigates away, etc.)
	async forceFlush(userId) {
		if (this.updateTimer) {
			clearTimeout(this.updateTimer);
			this.updateTimer = null;
		}
		
		await this.flushUpdatesToFirebase(userId);
	}
	
	// Getter for the maximum favorite players limit
	getMaxPlayersLimit() {
		return this.MAX_FAVORITE_PLAYERS;
	}
}

// Create singleton instance
const favoritesManager = new FavoritesManager();

export default favoritesManager; 